import {
  AdvancedOneIcon,
  FastForwardIcon,
  FontColorEnum,
  FontSizeEnum,
  FontWeightEnum,
  IconSizeEnum,
  MarkOutIcon,
  Tooltip,
  Typography,
} from '@acceso/design-system';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { SegmentTranscription } from '@/components/SegmentTranscription/SegmentTranscription';
import { ToolTipContent } from '@/components/ToolTipContent';
import { useBroadcastScheduleProgram } from '@/features/broadcast-schedule-program/context/BroadcastScheduleProgramContext';
import { useSegmentTranscriptionsPermissions } from '@/features/broadcast-schedule-program/hooks/useSegmentTranscriptionsPermissions';
import { TASRRecognitionResult, TBaseItemMention } from '@/features/segments/types';
import { useProgramBroadcastFilters } from '@/stores/program-broadcast-filters';
import { StatusEnrichmentEnum } from '@/types/types';

import { ESegmentToolkit } from '../../BroadcastScheduleProgramBodySegmentToolkit/types';

export type TTranscription = {
  id: string;
  words: TASRRecognitionResult[];
  mentions: TBaseItemMention[];
};
export function BroadcastScheduleProgramBodySegmentDetailCardBodySegmentTranscription() {
  const {
    activeSegment,
    playerVideoCurrentTime,
    textToHighlight,
    pinnedId,
    cutForm,
    videoControlRef,
    formBox,
    setSelectedMention,
    handleTooltikClick,
    setCutData,
    setFormBox,
    isParrillaSegments,
    handleDateTimeSegment,
    handleDateTimeClip,
  } = useBroadcastScheduleProgram();
  const { search } = useProgramBroadcastFilters();
  const { t } = useTranslation();

  const [transcriptionData, setTranscriptionData] = useState<TTranscription | null>(null);

  const { canSee: canSeeSegmentTranscriptions } = useSegmentTranscriptionsPermissions();

  useEffect(() => {
    if (!activeSegment || !activeSegment.segment_transcriptions[0]) setTranscriptionData(null);

    if (activeSegment && activeSegment.segment_transcriptions[0]) {
      const transcription: TTranscription = {
        id: activeSegment.segment_transcriptions[0].id,
        mentions: activeSegment.segment_mentions,
        words: activeSegment.segment_transcriptions[0].asr_recognition_results,
      };
      setTranscriptionData(transcription);
    }
  }, [activeSegment]);

  const uriName = useMemo(() => {
    const partes = activeSegment?.uri?.split('-') || '';
    const ultimaParte = partes[partes?.length - 1];
    return ultimaParte;
  }, [activeSegment?.uri]);

  const activeClips = useCallback(() => {
    setFormBox('cut');
    setCutData(null);
    if (!pinnedId || pinnedId !== ESegmentToolkit.CORTES) {
      handleTooltikClick(ESegmentToolkit.CORTES);
    }
  }, [handleTooltikClick, pinnedId]);

  const handleClickStartCut = () => {
    if (!pinnedId || (pinnedId !== ESegmentToolkit.CORTES && formBox === 'cut')) {
      handleTooltikClick(ESegmentToolkit.CORTES);
    }
    if (formBox !== 'cut') {
      activeClips();
    }

    if (isParrillaSegments) {
      handleDateTimeSegment(cutForm, 'start_datetime');
    } else {
      handleDateTimeClip(cutForm, 'start_datetime');
    }
  };

  const handleClickEndCut = () => {
    if (isParrillaSegments) {
      handleDateTimeSegment(cutForm, 'end_datetime');
    } else {
      handleDateTimeClip(cutForm, 'end_datetime');
    }
  };

  const handleClickRebobinar = () => {
    const newTime = Math.max(0, playerVideoCurrentTime - 5);
    videoControlRef.current?.seekToSecond(newTime);
  };

  const handleClickForward = () => {
    if (videoControlRef.current) {
      const newTime = Math.max(0, playerVideoCurrentTime + 5);
      videoControlRef.current?.seekToSecond(newTime);
    }
  };

  const handleClickBackFrame = () => {
    const frameDuration = 1 / 30;
    const newTime = Math.max(0, playerVideoCurrentTime - frameDuration);
    videoControlRef.current?.seekToSecond(newTime);
  };

  const handleClickForwardFrame = () => {
    const frameDuration = 1 / 30;
    const newTime = playerVideoCurrentTime + frameDuration;
    videoControlRef.current?.seekToSecond(newTime);
  };

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (!event.ctrlKey && !event.shiftKey && event.key === 'F2') {
        event.preventDefault();
        handleClickStartCut();
      }

      if (!event.ctrlKey && !event.shiftKey && event.key === 'F9') {
        event.preventDefault();
        handleClickEndCut();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [
    activeSegment,
    playerVideoCurrentTime,
    isParrillaSegments,
    handleDateTimeSegment,
    handleDateTimeClip,
  ]);

  return (
    <div className="max-h-calc[100vh-200px] flex flex-col gap-2 overflow-auto">
      <div className="flex items-center justify-between">
        <Typography
          fontSize={FontSizeEnum.Md}
          color={FontColorEnum.Dark10}
          weight={FontWeightEnum.Bold}
        >
          {uriName}
        </Typography>

        <div className="flex items-center gap-2 rounded-md bg-neutral-15 px-2 py-1 text-accent-active-primary-30">
          <Tooltip
            help={<ToolTipContent text="Rebobinar" keyword={'⬅'} modifiers={['Ctrl']} />}
            grow
          >
            <FastForwardIcon
              onClick={handleClickRebobinar}
              onMouseDown={e => e.preventDefault()}
              size={IconSizeEnum.SM}
              className="cursor-pointer"
            />
          </Tooltip>
          <Tooltip help={<ToolTipContent text="Rebobina frame a frame" keyword={','} />} grow>
            <AdvancedOneIcon
              onClick={handleClickBackFrame}
              onMouseDown={e => e.preventDefault()}
              size={IconSizeEnum.SM}
              className="-rotate-180 cursor-pointer"
            />
          </Tooltip>
          <Tooltip help={<ToolTipContent text="Avanza frame a frame" keyword={'.'} />} grow>
            <AdvancedOneIcon
              onClick={handleClickForwardFrame}
              onMouseDown={e => e.preventDefault()}
              size={IconSizeEnum.SM}
              className="cursor-pointer"
            />
          </Tooltip>
          <Tooltip help={<ToolTipContent text="Avanzar" keyword={'⮕'} modifiers={['Ctrl']} />} grow>
            <FastForwardIcon
              onClick={handleClickForward}
              onMouseDown={e => e.preventDefault()}
              size={IconSizeEnum.SM}
              className="-rotate-180 cursor-pointer"
            />
          </Tooltip>
          <Tooltip help={<ToolTipContent text="Iniciar corte" keyword={'F2'} />} grow>
            <MarkOutIcon
              size={IconSizeEnum.SM}
              onClick={handleClickStartCut}
              onMouseDown={e => e.preventDefault()}
              className="-rotate-180 cursor-pointer"
            />
          </Tooltip>
          <Tooltip help={<ToolTipContent text="Finalizar corte" keyword={'F9'} />} grow>
            <MarkOutIcon
              onClick={handleClickEndCut}
              onMouseDown={e => e.preventDefault()}
              size={IconSizeEnum.SM}
              className="cursor-pointer"
            />
          </Tooltip>
          {/* <Tooltip help={<ToolTipContent text="Actualizar" keyword={'To decide'} />} grow>
            <RedoPdfIcon size={IconSizeEnum.SM} className="cursor-pointer" />
          </Tooltip> */}
        </div>
      </div>

      {canSeeSegmentTranscriptions && transcriptionData && (
        <SegmentTranscription
          transcriptions={transcriptionData}
          seconds={playerVideoCurrentTime}
          textToHighlight={textToHighlight || ''}
          setSelectedMention={setSelectedMention}
          searchTextToHighlight={search}
        />
      )}
      {canSeeSegmentTranscriptions &&
      !transcriptionData &&
      (activeSegment?.parameters?.status_enrichments.stt?.status ===
        StatusEnrichmentEnum.PROCESSING ||
        activeSegment?.parameters?.status_enrichments.stt?.status ===
          StatusEnrichmentEnum.REPROCESSING) ? (
        <Typography
          fontSize={FontSizeEnum.Md}
          color={FontColorEnum.Dark10}
          weight={FontWeightEnum.Bold}
        >
          {t('Generando transcripción...')}
        </Typography>
      ) : (
        <></>
      )}
    </div>
  );
}
